import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Modal,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import VideoIcon from "@mui/icons-material/VideoLibrary";
import DescriptionIcon from "@mui/icons-material/Description";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import { db, storage } from "../../../Firebase/FirebaseConfig"; // Assuming Firebase is properly initialized
import moment from "moment";
import { child, increment, push, ref, set, update } from "firebase/database";
import {
  getDownloadURL,
  uploadBytes,
  ref as storageRef,
} from "firebase/storage";
import toast from "react-hot-toast";

const FormUserMessage = ({
  selectedNames,
  selectedIds,
  handleClose,
  loginProfile,
  setSelectedRows,
}) => {
  const [openPreview, setOpenPreview] = useState(false);
  const [message, setMessage] = useState("");
  const [topic, setTopic] = useState("");
  const [topicError, setTopicError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [fileError, setFileError] = useState("");
  const [selectedOption, setSelectedOption] = useState("Text");
  const [file, setFile] = useState(null);

  const handleSend = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select atleast one user");
      handleClose();
      return;
    }

    const OwnId = loginProfile?._id;
    if (!OwnId) {
      console.error("User ID is missing");
      return;
    }

    if (selectedOption.toLowerCase() === "text") {
      try {
        let sendMessageAdmin = {
          content: message,
          senderID: OwnId,
          receiverID: "",
          timestamp: moment().format("MMMM DD, YYYY"),
          time: moment().format("HH:mm"),
          dateTime: moment().format(),
          seen: false,
          type: selectedOption.toLowerCase(),
          userName: "Admin",
        };

        for (const memberId of selectedIds) {
          const receiverID = [OwnId, memberId].sort().join("-");
          const chatroomRef = ref(db, `chatrooms/${receiverID}`);
          const messagesRef = child(chatroomRef, "messages");
          sendMessageAdmin.receiverID = memberId;

          const newMessageRef = push(messagesRef);
          await set(newMessageRef, sendMessageAdmin);

          const updates = {};
          updates[`chatList/${OwnId}/${memberId}/chatroomID`] = receiverID;
          updates[`chatList/${OwnId}/${memberId}/lastMessage`] =
            sendMessageAdmin.content;
          updates[`chatList/${OwnId}/${memberId}/lastMessageTimestamp`] =
            sendMessageAdmin.dateTime;
          updates[`chatList/${OwnId}/${memberId}/lastMessaged`] = OwnId;
          updates[`chatList/${OwnId}/${memberId}/seenCount`] = 0;
          updates[`chatList/${OwnId}/${memberId}/type`] = "text";

          updates[`chatList/${memberId}/${OwnId}/chatroomID`] = receiverID;
          updates[`chatList/${memberId}/${OwnId}/lastMessage`] =
            sendMessageAdmin.content;
          updates[`chatList/${memberId}/${OwnId}/lastMessageTimestamp`] =
            sendMessageAdmin.dateTime;
          updates[`chatList/${memberId}/${OwnId}/lastMessaged`] = OwnId;
          updates[`chatList/${memberId}/${OwnId}/seenCount`] = increment(1);
          updates[`chatList/${memberId}/${OwnId}/type`] = "text";
          updates[`chatList/${memberId}/${OwnId}/userName`] = "Admin";
          updates[`chatList/${memberId}/${OwnId}/remoteId`] = OwnId;

          await update(ref(db), updates);
        }
        toast.success("Message sent");

        setOpenPreview(false);
        handleClose();
        setMessage("");
        setTopic("");
        setFile(null);
        setSelectedOption("Text");
        setTopicError("");
        setMessageError("");
        setFileError("");
        setSelectedRows({});
      } catch (error) {
        console.error("Error sending message:", error);
      }
    } else {
      if (!file) return;

      try {
        for (const memberId of selectedIds) {
          const receiverID = [OwnId, memberId].sort().join("-");

          const fileStorageRef = storageRef(
            storage,
            `uploads/${OwnId}/${file.name}`
          );
          const snapshot = await uploadBytes(fileStorageRef, file, {
            contentType: "application/octet-stream",
          });
          const downloadURL = await getDownloadURL(fileStorageRef);

          const fileData = {
            content: file.name,
            senderID: OwnId,
            receiverID: memberId,
            timestamp: moment().format("MMMM DD, YYYY"),
            time: moment().format("HH:mm"),
            dateTime: moment().format(),
            type: file.type.startsWith("image/")
              ? "image"
              : file.type.startsWith("video/")
              ? "video"
              : "file",
            fileUrl: downloadURL,
          };

          const chatroomRef = ref(db, `chatrooms/${receiverID}`);
          const messagesRef = child(chatroomRef, "messages");

          const newMessageRef = push(messagesRef);
          await set(newMessageRef, fileData);

          const updates = {};
          updates[`chatList/${OwnId}/${memberId}/chatroomID`] = receiverID;
          updates[`chatList/${OwnId}/${memberId}/lastMessage`] =
            fileData.content;
          updates[`chatList/${OwnId}/${memberId}/lastMessageTimestamp`] =
            fileData.dateTime;
          updates[`chatList/${OwnId}/${memberId}/lastMessaged`] = OwnId;
          updates[`chatList/${OwnId}/${memberId}/seenCount`] = 0;
          updates[`chatList/${OwnId}/${memberId}/type`] = fileData.type;

          updates[`chatList/${memberId}/${OwnId}/chatroomID`] = receiverID;
          updates[`chatList/${memberId}/${OwnId}/lastMessage`] =
            fileData.content;
          updates[`chatList/${memberId}/${OwnId}/lastMessageTimestamp`] =
            fileData.dateTime;
          updates[`chatList/${memberId}/${OwnId}/lastMessaged`] = OwnId;
          updates[`chatList/${memberId}/${OwnId}/seenCount`] = increment(1);
          updates[`chatList/${memberId}/${OwnId}/type`] = fileData.type;
          updates[`chatList/${memberId}/${OwnId}/fileUrl`] = fileData.fileUrl;

          await update(ref(db), updates);
        }
        toast.success("Message sent");

        setOpenPreview(false);
        handleClose();
        setMessage("");
        setTopic("");
        setFile(null);
        setSelectedOption("Text");
        setTopicError("");
        setMessageError("");
        setFileError("");
        setSelectedRows({});
      } catch (error) {
        console.error("Error sending file:", error);
      }
    }
  };

  const handlePreview = () => {
    if (selectedOption === "Text" && !message) {
      setMessageError("Message is required.");
      return;
    } else {
      setMessageError("");
    }

    if (selectedOption !== "Text" && !file) {
      setFileError("File is required.");
      return;
    } else {
      setFileError("");
    }

    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
  };

  const handleSendMessage = () => {
    handleSend();
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setMessage("");
    setFile(null);
    setFileError("");
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileError("");
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "40vw",
          mx: "auto",
          mt: 2,
          p: 3,
          border: "1px solid #ccc",
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "white",
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6">Selected</Typography>
        <TextField
          variant="filled"
          fullWidth
          label="Selected Users"
          value={selectedNames}
          multiline
          rows={1}
          InputProps={{
            readOnly: true,
            style: { whiteSpace: "normal", overflow: "hidden" },
          }}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "flex",
          }}
        />

        <Typography variant="h6">Select Message Type</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Text">
            <IconButton
              onClick={() => handleOptionSelect("Text")}
              sx={{
                backgroundColor:
                  selectedOption === "Text" ? "#007bff" : "inherit",
                color: selectedOption === "Text" ? "white" : "inherit",
              }}
            >
              <TextFieldsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Image">
            <IconButton
              onClick={() => handleOptionSelect("Image")}
              sx={{
                backgroundColor:
                  selectedOption === "Image" ? "#007bff" : "inherit",
                color: selectedOption === "Image" ? "white" : "inherit",
              }}
            >
              <ImageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Video">
            <IconButton
              onClick={() => handleOptionSelect("Video")}
              sx={{
                backgroundColor:
                  selectedOption === "Video" ? "#007bff" : "inherit",
                color: selectedOption === "Video" ? "white" : "inherit",
              }}
            >
              <VideoIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="File">
            <IconButton
              onClick={() => handleOptionSelect("File")}
              sx={{
                backgroundColor:
                  selectedOption === "File" ? "#007bff" : "inherit",
                color: selectedOption === "File" ? "white" : "inherit",
              }}
            >
              <DescriptionIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {selectedOption === "Text" && (
          <>
            <Typography variant="h6">Message</Typography>
            <TextField
              variant="outlined"
              fullWidth
              label="Message"
              placeholder="Enter your message here"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              error={!!messageError}
              helperText={messageError}
              multiline
              rows={4}
            />
          </>
        )}

        {selectedOption !== "Text" && (
          <>
            <Typography variant="h6">Select File</Typography>
            <input
              accept={
                selectedOption === "Image"
                  ? "image/*"
                  : selectedOption === "Video"
                  ? "video/*"
                  : "*"
              }
              type="file"
              onChange={handleFileChange}
            />
            {fileError && (
              <Typography variant="caption" color="error">
                {fileError}
              </Typography>
            )}
          </>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSendMessage}
          sx={{ mt: 2 }}
        >
          Send Message
        </Button>
      </Box>
    </>
  );
};

export default FormUserMessage;
